import { NetworkIcon } from "lucide-react";
import { useCallback } from "react";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  BlockContainerShapeContext,
  NodeBlockHeader,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasInput,
  CanvasSimpleSelect,
} from "@/components/canvas-editor/components/Inputs";
import { SocialGraphPreview } from "@/components/canvas-editor/components/SocialGraphPreview";
import { NodeDocs } from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  SocialGraphShape,
  socialGraphType,
  inclusionOperators,
  relationshipTypes,
  InclusionOperator,
  RelationshipType,
  SocialGraphProps,
  Dimensions,
  inclusionOperatorOptions,
  metadataType,
} from "@/components/canvas-editor/shapes/shapes";
import { MIN_H, ToolStateNode } from "@/components/canvas-editor/utils";
import { SimpleTooltip } from "@/components/ui/tooltip";


export function isSocialGraphShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is SocialGraphShape {
  return shape.type === socialGraphType;
}

export class SocialGraphTool extends ToolStateNode {
  static override id = socialGraphType;

  static override uiToolDef = (editor: Editor) => ({
    id: socialGraphType,
    icon: NetworkIcon,
    label: SocialGraphShapeUtil.docs.title,
    // kbd: "s",
    defaultColor: SocialGraphShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(socialGraphType),
    docs: () => (
      <NodeDocs
        editor={editor}
        component={SocialGraph}
        util={SocialGraphShapeUtil}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: socialGraphType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class SocialGraphShapeUtil extends BindableUtil<SocialGraphShape> {
  static override migrations = createMigrations(socialGraphType);
  static override type = socialGraphType;

  static override docs = {
    defaultColor: "green" as const,
    title: "Social Graph",
    description: "Included posts from a users follower/following lists.",
  };

  override getType() {
    return socialGraphType;
  }

  static override props: RecordProps<SocialGraphShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    username: T.string,
    operator: T.literalEnum(...inclusionOperators),
    direction: T.literalEnum(...relationshipTypes),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 500,
      minW: 500,
      h: MIN_H,
      username: "jay.bsky.team",
      operator: inclusionOperators[0],
      direction: relationshipTypes[0],
      metadata: null,
    };
  }

  override component(shape: SocialGraphShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <SocialGraph
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function SocialGraph({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: SocialGraphShape;
  className?: string;
  updateShape: (
    shape: SocialGraphShape,
    props: Partial<SocialGraphProps>,
  ) => void;
}) {
  const variant = shape.props.operator === "in" ? "green" : "red";
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={shape.props.metadata?.color || variant}
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader props={shape.props} docs={SocialGraphShapeUtil.docs} />
        <BlockBody>
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            <div className="flex gap-2">
              <CanvasSimpleSelect
                className="w-28"
                value={shape.props.operator}
                onValueChange={(v) =>
                  updateShape(shape, { operator: v as InclusionOperator })
                }
                options={inclusionOperatorOptions}
                aria-label="Inclusion Operator"
              />
              <SimpleTooltip message="The username to pull followers/follows from.">
                <CanvasInput
                  aria-label="Username"
                  value={shape.props.username}
                  placeholder="Username"
                  className="flex-1"
                  onValueChange={(username) => updateShape(shape, { username })}
                  onBlur={() => {
                    updateShape(shape, {
                      username: shape.props.username.trim().replace(/^@/g, ""),
                    });
                  }}
                />
              </SimpleTooltip>
              <CanvasSimpleSelect
                aria-label="Relationship Type"
                className="w-[108px]"
                value={shape.props.direction}
                onValueChange={(v) =>
                  updateShape(shape, { direction: v as RelationshipType })
                }
                options={relationshipTypes}
              />
            </div>

            <SocialGraphPreview
              username={shape.props.username}
              direction={shape.props.direction}
            />
          </div>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
